import { useEffect, useMemo, useState } from "react";
import {
  Activity, AlertCircle, ArrowRight, Banknote, FileClock, Gift, RefreshCw,
  ShieldCheck, Users, Wrench
} from "lucide-react";
import {
  getAdminDashboardSummary, getAdminGifts, getAdminOperations,
  getAdminTransactions, getAdminUsers, getMaintenanceControl
} from "../lib/api";
import { DASHBOARD_WINDOWS, summarizeGifts, summarizeKyc, summarizeTransactions, unavailableDashboard } from "../lib/dashboard";
import { kycStage } from "../lib/kycOps";
import { PageHeading } from "./PageHeading";
import { Skeleton } from "./Skeleton";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { DEPLOYMENT_NAME } from "../lib/runtime";
import type {
  AdminDashboardSummary, AdminOperation,
  AdminUserSummary, DashboardWindow, MaintenanceControl, PortalView, Viewer
} from "../types";

interface OverviewFallbacks {
  users: AdminUserSummary[];
  transactionTotal: number;
  giftTotal: number | null;
  operations: AdminOperation[];
  maintenance: MaintenanceControl | null;
}

const emptyFallbacks: OverviewFallbacks = {
  users: [], transactionTotal: 0, giftTotal: null,
  operations: [], maintenance: null
};

const actionLabel = (value: string) => value
  .replaceAll(".", " ")
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replace(/^./, (letter) => letter.toUpperCase());

function metricValue(value: number | null, loading: boolean) {
  if (loading) return <Skeleton width="62px" height="24px" />;
  return value === null ? "—" : value.toLocaleString();
}

function unavailableReason(reason: string | undefined) {
  if (reason === "not_deployed") return "Awaiting the GCP summary contract";
  if (reason === "not_indexed") return "Nightly registration index not built";
  if (reason === "timeout") return "Source exceeded its dashboard deadline";
  return "Source currently unavailable";
}

export function OverviewPage({ viewer, onNavigate }: { viewer: Viewer; onNavigate: (view: PortalView) => void }) {
  const { timeZone } = useTimeZone();
  const [windowKey, setWindowKey] = useState<DashboardWindow>("7d");
  const [summary, setSummary] = useState<AdminDashboardSummary>(() => unavailableDashboard("7d"));
  const [fallbacks, setFallbacks] = useState<OverviewFallbacks>(emptyFallbacks);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState<string[]>([]);

  const load = async (window: DashboardWindow) => {
    setLoading(true);
    setFailedSources([]);
    const results = await Promise.allSettled([
      getAdminDashboardSummary(window),
      getAdminUsers(),
      getAdminTransactions(),
      getAdminGifts(),
      getAdminOperations(),
      getMaintenanceControl()
    ] as const);
    const labels = ["Dashboard summary", "Users", "Transactions", "Gifts", "Audit activity", "App controls"];
    setFailedSources(results.flatMap((result, index) => result.status === "rejected" ? [labels[index]] : []));
    const [dashboard, users, transactions, gifts, operations, maintenance] = results;
    setSummary(dashboard.status === "fulfilled" ? dashboard.value : unavailableDashboard(window));
    setFallbacks({
      users: users.status === "fulfilled" ? users.value.users : [],
      transactionTotal: transactions.status === "fulfilled" ? transactions.value.totalCount : 0,
      giftTotal: gifts.status === "fulfilled" ? gifts.value.totalCount : null,
      operations: operations.status === "fulfilled" ? operations.value.operations : [],
      maintenance: maintenance.status === "fulfilled" ? maintenance.value : null
    });
    setLoading(false);
  };

  useEffect(() => { void load(windowKey); }, [viewer.role, windowKey]);

  const windowLabel = DASHBOARD_WINDOWS.find((item) => item.key === windowKey)?.label ?? "Last 7 days";
  const kyc = summary.kyc.available ? summarizeKyc(summary.kyc.data) : null;
  const gifts = summary.gifts.available ? summarizeGifts(summary.gifts.data) : null;
  const transactions = summary.transactions.available ? summarizeTransactions(summary.transactions.data) : null;
  const sampleKycAttention = useMemo(() => fallbacks.users.filter((user) => {
    return ["customerAction", "blocked", "unknown"].includes(kycStage(user.state));
  }).length, [fallbacks.users]);
  const partialSections = [summary.registrations, summary.operations, summary.kyc, summary.gifts, summary.transactions].filter((section) => !section.available).length;
  const registrationStale = summary.registrations.available && Date.now() - Date.parse(summary.registrations.asOf) > 36 * 60 * 60 * 1000;
  const verificationCoveragePartial = summary.kyc.available
    && summary.kyc.data.verificationCoverageStartedAt !== null
    && Date.parse(summary.kyc.data.verificationCoverageStartedAt) > Date.parse(summary.window.startsAt);
  const firstName = viewer.email.split("@")[0].replace(/^./, (letter) => letter.toUpperCase());

  const activityMetrics = [
    {
      label: "New registered users", value: summary.registrations.available ? summary.registrations.data.createdInWindow : null,
      detail: summary.registrations.available ? `${summary.registrations.data.currentTotal.toLocaleString()} Firebase Auth accounts · indexed ${formatTimestamp(summary.registrations.asOf, timeZone)}` : unavailableReason(summary.registrations.reason),
      icon: Users, view: "users" as const
    },
    {
      label: "KYC started", value: summary.kyc.available ? summary.kyc.data.startedInWindow : null,
      detail: summary.kyc.available ? `${summary.kyc.data.verifiedInWindow.toLocaleString()} first verifications observed${verificationCoveragePartial ? ` · tracking since ${formatTimestamp(summary.kyc.data.verificationCoverageStartedAt, timeZone)}` : ""}` : `${unavailableReason(summary.kyc.reason)} · ${sampleKycAttention} need attention in ${fallbacks.users.length} loaded users`,
      icon: ShieldCheck, view: "kyc" as const
    },
    {
      label: "Funded gifts sent", value: gifts?.createdInWindow ?? null,
      detail: gifts ? `${gifts.currentTotal.toLocaleString()} funded gifts currently indexed` : `${unavailableReason(summary.gifts.available ? undefined : summary.gifts.reason)}${fallbacks.giftTotal === null ? "" : ` · ${fallbacks.giftTotal} total gift rows`}`,
      icon: Gift, view: "cards" as const
    },
    {
      label: "Transactions created", value: transactions?.createdInWindow ?? null,
      detail: transactions ? windowLabel : `${unavailableReason(summary.transactions.available ? undefined : summary.transactions.reason)} · ${fallbacks.transactionTotal.toLocaleString()} total ledger rows`,
      icon: Banknote, view: "transactions" as const
    }
  ];

  const recentOperations = [...fallbacks.operations]
    .sort((left, right) => (right.requestedAt ?? "").localeCompare(left.requestedAt ?? ""))
    .slice(0, 6);

  return <div className="page-stack overview-page">
    <PageHeading
      eyebrow="OPERATIONAL OVERVIEW"
      title={`Good day, ${firstName}`}
      description="What needs attention now, followed by a concise activity pulse."
      info={{ label: "dashboard coverage", children: <><strong>Counts are never inferred from an unlabeled sample.</strong> Unavailable aggregate sections remain visible, and capped fallback windows identify their scope.</> }}
      actions={<div className="overview-heading-actions"><label>Window<select value={windowKey} onChange={(event) => setWindowKey(event.target.value as DashboardWindow)}>{DASHBOARD_WINDOWS.map((item) => <option value={item.key} key={item.key}>{item.label}</option>)}</select></label><button className="secondary-button" onClick={() => void load(windowKey)} disabled={loading}><RefreshCw size={16} className={loading ? "spin" : undefined} />Refresh</button></div>}
    />

    <section className={`overview-status-strip ${failedSources.length || partialSections || registrationStale ? "status-partial" : ""}`} aria-live="polite">
      <Activity size={17} /><span><strong>{failedSources.length ? "Partial snapshot" : registrationStale ? "Registration index is stale" : partialSections ? "Some metrics awaiting authoritative sources" : "Operational sources responding"}</strong><small>Generated {formatTimestamp(summary.generatedAt, timeZone)} · {windowLabel}</small></span><i className={failedSources.length || partialSections || registrationStale ? "health-warn" : "health-ok"} />
    </section>

    {fallbacks.maintenance?.enabled ? <div className="maintenance-banner" role="alert"><Wrench size={20} /><div><strong>{DEPLOYMENT_NAME} maintenance mode is active</strong><span>Automatic expiry {formatTimestamp(fallbacks.maintenance.expiresAt, timeZone)} · {fallbacks.maintenance.reason || "No reason recorded"}</span></div><button onClick={() => onNavigate("controls")}>Review control <ArrowRight size={15} /></button></div> : null}
    {failedSources.length ? <div className="error-panel"><AlertCircle size={19} /><div><strong>Some sources did not respond</strong><p>{failedSources.join(", ")}. Other sections remain independently current.</p></div></div> : null}

    <div className="overview-section-title"><div><p className="section-label">ACTIVITY PULSE</p><h2>{windowLabel}</h2></div><span>Counts only · gift and transaction populations overlap</span></div>
    <section className="overview-metric-grid" aria-busy={loading}>
      {activityMetrics.map(({ label, value, detail, icon: Icon, view }) => <button key={label} className={`overview-metric ${value === null ? "metric-unavailable" : ""}`} onClick={() => onNavigate(view)}><span className="overview-metric-icon"><Icon size={20} /></span><span className="overview-metric-copy"><small>{label}</small><strong>{metricValue(value, loading)}</strong><em>{detail}</em></span><ArrowRight size={16} className="metric-arrow" /></button>)}
    </section>

    <section className="history-card overview-activity">
      <div className="section-heading-row"><div><p className="section-label">AUDIT VISIBILITY</p><h2>Recent admin activity</h2></div><button className="table-action" onClick={() => onNavigate("audit")}>View audit trail</button></div>
      <div className="activity-list">
        {!loading && recentOperations.length === 0 ? <div className="overview-empty"><FileClock size={24} /><span>No privileged operations recorded yet.</span></div> : null}
        {recentOperations.map((operation) => <button key={operation.id} className="activity-row" onClick={() => onNavigate("audit")}><span className={`activity-state status-${operation.state}`}><FileClock size={15} /></span><span><strong>{actionLabel(operation.action)}</strong><small>{operation.actorEmail || "Unknown actor"} · {operation.reason || "No reason"}</small></span><time>{formatTimestamp(operation.requestedAt, timeZone)}</time></button>)}
      </div>
    </section>
  </div>;
}
